<template>
  <BaseSelect v-model="currentlySelectedOption" :options="selectNumberColumnOperators" :append-to-body="true"></BaseSelect>
  <template v-if="numberFilter.option === NumberColumnOperators.BETWEEN">
    <BaseInput
      v-model.number="lowerNumberInput"
      type="number"
      :placeholder="t('genericTable.numberFilter.enterMin', { description: props.description })"
    >
    </BaseInput>
    <BaseInput
      v-model.number="upperNumberInput"
      type="number"
      :placeholder="t('genericTable.numberFilter.enterMax', { description: props.description })"
    >
    </BaseInput>
  </template>
  <BaseInput
    v-if="numberFilter.option === NumberColumnOperators.SMALLER_THEN"
    v-model.number="singleValueInput"
    type="number"
    :placeholder="t('genericTable.numberFilter.enterUpperBound', { description: props.description })"
  >
  </BaseInput>
  <BaseInput
    v-if="numberFilter.option === NumberColumnOperators.LARGER_THEN"
    v-model.number="singleValueInput"
    type="number"
    :placeholder="t('genericTable.numberFilter.enterLowerBound', { description: props.description })"
  >
  </BaseInput>
  <BaseInput
    v-if="numberFilter.option === NumberColumnOperators.EXACT"
    v-model.number="singleValueInput"
    type="number"
    :placeholder="t('genericTable.numberFilter.enterValue', { description: props.description })"
  >
  </BaseInput>
  <BaseSelect
    v-if="numberFilter.option === NumberColumnOperators.IN"
    v-model="arrayValueInput"
    mode="tags"
    :placeholder="t('genericTable.numberFilter.enterValues', { description: props.description })"
    :append-to-body="true"
    searchable
    :is-clearable="true"
    :create-option="true"
  >
  </BaseSelect>
  <ValueExistsFilter v-if="numberFilter.option === CommonColumnOperators.VALUE_EXISTS" v-model="numberFilter"></ValueExistsFilter>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import BaseInput from "../../../../components/BaseInput.vue";
import BaseSelect from "../../../../components/BaseSelect.vue";
import { CommonColumnOperators, NumberColumnOperators } from "../../enum.filterOperators";
import { NumberFilterType } from "../../type.genericTableQuery";
import ValueExistsFilter from "./ValueExistsFilter.vue";
const { t } = useI18n();
const props = defineProps<{ description: string }>();
const numberFilterType = defineModel<NumberFilterType>({ required: true });
const numberFilter = computed(() => numberFilterType.value.filter);

const tempLowerNumber = ref<"" | number>(NaN);
const tempUpperNumber = ref<"" | number>(NaN);

const selectNumberColumnOperators = [
  { value: NumberColumnOperators.BETWEEN, label: t("genericTable.numberFilter.between") },
  { value: NumberColumnOperators.SMALLER_THEN, label: t("genericTable.numberFilter.smallerThen") },
  { value: NumberColumnOperators.LARGER_THEN, label: t("genericTable.numberFilter.largerThen") },
  { value: NumberColumnOperators.EXACT, label: t("genericTable.numberFilter.exact") },
  { value: NumberColumnOperators.IN, label: t("genericTable.numberFilter.in") },
  { value: CommonColumnOperators.VALUE_EXISTS, label: t("genericTable.valueExistsFilter.valueExists") },
];

const currentlySelectedOption = computed({
  get() {
    return numberFilter.value.option;
  },
  set(newOption) {
    if (newOption !== numberFilter.value.option) {
      numberFilter.value.value = undefined;
      numberFilter.value.option = newOption;
    }
  },
});

const singleValueInput = computed({
  get: () => (typeof numberFilter.value.value === "number" ? numberFilter.value.value : ""),
  set: (val: "" | number) => {
    numberFilter.value.value = typeof val === "string" ? undefined : val;
  },
});

const lowerNumberInput = computed({
  get: () => tempLowerNumber.value,
  set: (val: "" | number) => {
    tempLowerNumber.value = val;
    updateBetween();
  },
});

const upperNumberInput = computed({
  get: () => tempUpperNumber.value,
  set: (val: "" | number) => {
    tempUpperNumber.value = val;
    updateBetween();
  },
});

function updateBetween() {
  if (tempLowerNumber.value !== "" && tempUpperNumber.value !== "") {
    numberFilter.value.value = [tempLowerNumber.value, tempUpperNumber.value];
  } else {
    numberFilter.value.value = undefined;
  }
}

const arrayValueInput = computed({
  get: () => (Array.isArray(numberFilter.value.value) ? numberFilter.value.value.map(String) : []),
  set: (val: string[]) => {
    const numbers = val.map(Number).filter((n) => !isNaN(n));
    numberFilter.value.value = numbers.length === 0 ? undefined : numbers;
  },
});
</script>
